import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Stack,
  Button,
  Container,
  Typography,
  Box,
} from "@mui/material";
import WeeklyLeaderboard from "../components/WeeklyLeaderboard";
import StagesTable from "../components/StagesTable";
import StagePeriodSelector from "../components/StagePeriodSelector";
import Iconify from "../components/iconify";
import { useSearchParams } from "react-router-dom";
import { get, post } from "src/utils/networkUtils";
import StageDialog from "src/components/stage-admin/StageDialog";
import StagePeriodDialog from "src/components/stage-admin/StagePeriodDialog";
import useWithUrlTenant from "src/hooks/useTenant";

// ----------------------------------------------------------------------

export default function StagesPeriodsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openStageDialog, setOpenStageDialog] = useState(false);
  const [openStagePeriodDialog, setOpenStagePeriodDialog] = useState(false);
  const [stage, setStage] = useState(null);
  const [stagePeriod, setStagePeriod] = useState(null);
  const [stagePeriods, setStagePeriods] = useState(null);
  const [selectedStagePeriodId, setselectedStagePeriodId] = useState(null);
  const [selectedStagePeriod, setselectedStagePeriod] = useState(null);
  const withTenant = useWithUrlTenant();
  const [searchParams] = useSearchParams();

  // Get tenantId from URL params for tracking changes
  const tenantId = searchParams.get("tenantId");

  // Get tenants from Redux store and find current tenant
  const tenants = useSelector((state) => state.tenant.tenants);
  const currentTenant = tenants.find((tenant) => tenant.tenantId === parseInt(tenantId));
  const tenantName = currentTenant?.name || "Unknown Tenant";

  // Reset state when tenantId changes to avoid stale data
  useEffect(() => {
    setStagePeriods(null);
    setRows([]);
    setselectedStagePeriodId(null);
    setselectedStagePeriod(null);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return; // Don't fetch if no tenantId

    get(withTenant(`/admin/stage-periods?offset=0&limit=100`)).then(function (
      response
    ) {
      const allOption = {
        stagePeriodId: "all",
        name: "All",
        startDate: null,
        endDate: null,
      };
      const periodsWithAll = [allOption, ...response.stagePeriods];
      setStagePeriods(periodsWithAll);

      if (response.stagePeriods.length > 0) {
        console.log("Stage periods found:", response.stagePeriods);
        // First try to find a period that is currently active (live)
        let latestPeriod = response.stagePeriods.find((period) => {
          const startDate = new Date(period.startDate);
          const endDate = new Date(period.endDate);
          const now = new Date();
          return now >= startDate && now <= endDate;
        });

        // If no active period found, find the most recent upcoming period
        if (!latestPeriod) {
          const now = new Date();
          const upcomingPeriods = response.stagePeriods.filter((period) => {
            const startDate = new Date(period.startDate);
            return startDate > now;
          });

          if (upcomingPeriods.length > 0) {
            // Sort by start date and get the earliest upcoming period
            latestPeriod = upcomingPeriods.sort((a, b) =>
              new Date(a.startDate) - new Date(b.startDate)
            )[0];
          }
        }

        // If still no period found, get the most recent completed period
        if (!latestPeriod) {
          const now = new Date();
          const completedPeriods = response.stagePeriods.filter((period) => {
            const endDate = new Date(period.endDate);
            return endDate < now;
          });

          if (completedPeriods.length > 0) {
            // Sort by end date and get the most recently completed period
            latestPeriod = completedPeriods.sort((a, b) =>
              new Date(b.endDate) - new Date(a.endDate)
            )[0];
          }
        }

        // Final fallback to first period if nothing else works
        if (!latestPeriod) {
          console.log("No active, upcoming, or completed periods found, defaulting to first period.");
          latestPeriod = response.stagePeriods[0];
        }

        setselectedStagePeriodId(latestPeriod.stagePeriodId);
        setselectedStagePeriod(latestPeriod);
        console.log("Selected latest period:", latestPeriod);
        get(
          withTenant(
            `/admin/stage-periods/${latestPeriod.stagePeriodId}/stages`
          )
        ).then(function (response) {
          setRows(response.stages);
        }).catch((error) => {
          console.error("Error fetching stages for latest period:", error);
          setRows([]);
        });
      } else {
        console.log("No stage periods found for tenant:", tenantId);
        // If no stage periods, clear everything
        setselectedStagePeriodId(null);
        setselectedStagePeriod(null);
        setRows([]);
      }
    }).catch((error) => {
      console.error("Error fetching stage periods:", error);
      setStagePeriods([]);
      setRows([]);
    });
  }, [withTenant, tenantId]);

  useEffect(() => {
    // Only fetch stages if we have all required data and avoid stale requests
    if (!tenantId || !selectedStagePeriodId || !stagePeriods) {
      setRows([]);
      return;
    }

    // Verify that the selectedStagePeriodId exists in the current stagePeriods
    const periodExists = stagePeriods.some(period => period.stagePeriodId === selectedStagePeriodId);
    if (!periodExists) {
      setRows([]);
      return;
    }

    if (selectedStagePeriodId === "all") {
      get(withTenant(`/admin/stages?offset=0&limit=100`)).then(function (
        response
      ) {
        setRows(response.stages || []);
      }).catch((error) => {
        console.error("Error fetching all stages:", error);
        setRows([]);
      });
    } else {
      get(
        withTenant(`/admin/stage-periods/${selectedStagePeriodId}/stages`)
      ).then(function (response) {
        setRows(response.stages || []);
      }).catch((error) => {
        console.error("Error fetching stages for period:", error);
        setRows([]);
      });
    }
  }, [selectedStagePeriodId, withTenant, tenantId, stagePeriods]);
  const handlePeriodChange = (event) => {
    setLoading(true);
    setselectedStagePeriodId(event.target.value);
    setselectedStagePeriod(
      stagePeriods.find((period) => period.stagePeriodId === event.target.value)
    );
    setLoading(false);
  };

  const handleOpenEditStageDialog = (stageId) => {
    const stage = rows.find((item) => item.stageId === +stageId);
    console.log(stage, stageId);
    if (stage) {
      setStage(stage);
      setOpenStageDialog(true);
    }
  };

  const handleOpenEditStagePeriodDialog = () => {
    const stagePeriod = stagePeriods.find(
      (item) => item.stagePeriodId === selectedStagePeriodId
    );
    console.log(stagePeriod, stagePeriods, selectedStagePeriodId);
    if (stagePeriod) {
      setStagePeriod(stagePeriod);
      setOpenStagePeriodDialog(true);
    }
  };

  const closeStageDialog = () => {
    setOpenStageDialog(false);
    setStage(null);
  };

  const closeStagePeriodDialog = () => {
    setOpenStagePeriodDialog(false);
    setStagePeriod(null);
  };

  const handleSuccessStageDialog = () => {
    window.location.reload(true);
  };

  const payPrizeHandler = () => {
    setLoading(true);
    post(`/admin/stage-periods/${selectedStagePeriodId}/pay-prizes/${tenantId}`).then(
      function (response) {
        window.location.reload(true);
      }
    );
  };

  return (
    <>
      <Helmet>
        <title> Stages Periods </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {tenantName} - Stages
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={() => setOpenStagePeriodDialog(true)}
              sx={{ mr: 2 }}
            >
              Create Stage Period
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={() => setOpenStageDialog(true)}
            >
              Create Stage
            </Button>
          </Box>
        </Stack>

        <StagePeriodSelector
          stagePeriods={stagePeriods}
          selectedStagePeriodId={selectedStagePeriodId}
          onPeriodChange={handlePeriodChange}
          onEditStagePeriod={handleOpenEditStagePeriodDialog}
        />

        <StagesTable
          rows={rows}
          loading={loading}
          stagePeriods={stagePeriods}
          onEditStage={handleOpenEditStageDialog}
        />

        {selectedStagePeriodId && (
          <WeeklyLeaderboard
            selectedStagePeriod={selectedStagePeriod}
            selectedStagePeriodId={selectedStagePeriodId}
            tenantId={tenantId}
            payPrizeHandler={payPrizeHandler}
          />
        )}
      </Container>

      {openStageDialog && (
        <StageDialog
          stage={stage}
          open={openStageDialog}
          tenantId={tenantId}
          onSuccessHandler={handleSuccessStageDialog}
          onClose={closeStageDialog}
        />
      )}
      {openStagePeriodDialog && (
        <StagePeriodDialog
          stagePeriod={stagePeriod}
          open={openStagePeriodDialog}
          tenantId={tenantId}
          onSuccessHandler={handleSuccessStageDialog}
          onClose={closeStagePeriodDialog}
        />
      )}
    </>
  );
}
